import React from 'react';
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  Divider,
  Box,
  ActionIcon,
  Paper,
  Switch,
} from '@mantine/core';
import { IconGripVertical, IconEye, IconEyeOff } from '@tabler/icons-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  required?: boolean; // Some columns should always be visible
}

interface Props {
  opened: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  onSave: (columns: ColumnConfig[]) => void;
}

export default function ColumnSettingsModal({
  opened,
  onClose,
  columns,
  onSave,
}: Props) {
  const [localColumns, setLocalColumns] = React.useState<ColumnConfig[]>(columns);

  React.useEffect(() => {
    if (opened) {
      setLocalColumns(columns);
    }
  }, [opened, columns]);

  const handleToggleVisibility = (key: string) => {
    setLocalColumns((prev) =>
      prev.map((col) =>
        col.key === key && !col.required
          ? { ...col, visible: !col.visible }
          : col
      )
    );
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(localColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalColumns(items);
  };

  const handleSelectAll = () => {
    setLocalColumns((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  const handleDeselectAll = () => {
    setLocalColumns((prev) =>
      prev.map((col) => ({
        ...col,
        visible: col.required ? true : false,
      }))
    );
  };

  const handleSave = () => {
    onSave(localColumns);
    onClose();
  };

  const handleReset = () => {
    // Reset to default (all visible)
    const resetColumns = columns.map((col) => ({ ...col, visible: true }));
    setLocalColumns(resetColumns);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Customize Table Columns"
      centered
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Drag to reorder columns, toggle visibility using the switch
        </Text>

        <Group justify="space-between">
          <Button size="xs" variant="light" onClick={handleSelectAll}>
            Show All
          </Button>
          <Button size="xs" variant="light" onClick={handleDeselectAll}>
            Hide All
          </Button>
          <Button size="xs" variant="light" onClick={handleReset}>
            Reset Order
          </Button>
        </Group>

        <Divider />

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="columns">
            {(provided, snapshot) => (
              <Box
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : 'transparent',
                  padding: 4,
                  borderRadius: 8,
                  minHeight: 200,
                }}
              >
                {localColumns.map((col, index) => (
                  <Draggable
                    key={col.key}
                    draggableId={col.key}
                    index={index}
                    isDragDisabled={col.required}
                  >
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        shadow="xs"
                        p="sm"
                        mb="xs"
                        style={{
                          ...provided.draggableProps.style,
                          backgroundColor: snapshot.isDragging ? '#e7f5ff' : 'white',
                          border: snapshot.isDragging ? '2px solid #228be6' : '1px solid #dee2e6',
                          cursor: col.required ? 'not-allowed' : 'grab',
                          opacity: col.required ? 0.6 : 1,
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap="sm" wrap="nowrap">
                            <Box
                              {...provided.dragHandleProps}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: col.required ? 'not-allowed' : 'grab',
                              }}
                            >
                              <IconGripVertical
                                size={20}
                                style={{ color: col.required ? '#adb5bd' : '#868e96' }}
                              />
                            </Box>
                            
                            <Box>
                              <Text fw={500} size="sm">
                                {col.label}
                              </Text>
                              {col.required && (
                                <Text size="xs" c="dimmed">
                                  Required column
                                </Text>
                              )}
                            </Box>
                          </Group>

                          <Group gap="xs" wrap="nowrap">
                            {col.visible ? (
                              <IconEye size={18} style={{ color: '#228be6' }} />
                            ) : (
                              <IconEyeOff size={18} style={{ color: '#adb5bd' }} />
                            )}
                            
                            <Switch
                              checked={col.visible}
                              onChange={() => handleToggleVisibility(col.key)}
                              disabled={col.required}
                              size="md"
                            />
                          </Group>
                        </Group>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        <Divider />

        <Text size="xs" c="dimmed" ta="center">
          💡 Tip: Drag the grip icon to reorder columns, use the switch to show/hide
        </Text>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}